import { isMobileBrowser } from '@psl/components/styles/responsive';
import { MediaFileInfo } from '@psl/domain/models/MediaFileInfo';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { useEffect } from 'react';
import { TouchableNativeFeedback, View } from 'react-native';
import { wrapNavigatorScreen } from '../../functions/navigation/utils/wrapNavigatorScreen';

type MediaType = 'photo' | 'video';
const AllowedExtensions: { [p in MediaType]: RegExp } = {
  photo: /\.(jpe?g|png)$/i,
  video: /\.(mp4|mov|m4v)$/i,
};

const ErrorMessages: { [p in MediaType]: string } = {
  photo: 'Unsupported image file, where only JPEG or PNG files are allowed!',
  video: 'Unsupported video file, where only MP4 video files are allowed!',
};

const createUpload = (
  type: MediaType,
  options?: {
    allowedExtensions?: RegExp;
  },
) => {
  let lock = false;
  return new Promise<File>((resolve, reject) => {
    // create input file
    const el = document.createElement('input');
    el.id = (+new Date()).toString();
    el.style.display = 'none';
    el.setAttribute(
      'accept',
      type === 'photo' ? 'image/png, image/jpeg' : 'video/mp4',
    );
    el.setAttribute('type', 'file');
    document.body.appendChild(el);

    const cleanup = () => {
      const node = document.getElementById(el.id);
      node && document.body.removeChild(node);
    };

    if (isMobileBrowser()) {
      el.addEventListener('cancel', () => {
        cleanup();
        reject();
      });
    } else {
      el.addEventListener('click', () => {
        // file blur
        window.addEventListener(
          'focus',
          () => {
            setTimeout(() => {
              if (!lock && document.getElementById(el.id)) {
                reject(new Error('onblur'));
                // remove dom
                document.body.removeChild(document.getElementById(el.id)!);
              }
            }, 1000);
          },
          { once: true },
        );
      });
    }

    el.addEventListener(
      'change',
      () => {
        lock = true;

        try {
          const file = el.files?.[0];

          if (file == null) {
            reject(new Error('No file selected'));
            return;
          }

          if (
            file?.name != null &&
            !(options?.allowedExtensions ?? AllowedExtensions[type]).test(
              file.name,
            )
          ) {
            alert(ErrorMessages[type]);
            reject('Unsupported file type');
            return;
          }

          resolve(file);
        } finally {
          cleanup();
        }
      },
      { once: true },
    );

    // open file select box
    el.click();
  });
};

const CameraScreenPage = wrapNavigatorScreen(
  ({
    type,
    allowedExtensions,
    withObjectSize,
    onSelected,
  }: {
    type: 'photo' | 'video';
    withObjectSize?: boolean;
    allowedExtensions?: RegExp;
    onSelected: (photoUrl: string, file: MediaFileInfo) => Promise<void>;
  }) => {
    const navigation = useNavigation<StackNavigationProp<any>>();
    useEffect(() => {
      createUpload(type, { allowedExtensions }).then(
        (value: File) => {
          navigation.pop();
          const result = URL.createObjectURL(value as any);

          const invokeCallback = (
            width?: number | undefined,
            height?: number | undefined,
          ) => {
            return onSelected(result, {
              type,
              uri: result,
              width: width ?? 0,
              height: height ?? 0,
              size: value.size,
              name: value.name,
              ...{ file: value },
            });
          };

          if (type === 'photo' && withObjectSize) {
            return new Promise(resolve => {
              const img = new Image();
              img.onload = () => {
                resolve(invokeCallback(img.naturalWidth, img.naturalHeight));
              };
              img.onerror = () => {
                resolve(invokeCallback());
              };
              img.src = result;
            });
          } else if (type === 'video' && withObjectSize) {
            return new Promise(resolve => {
              const video = document.createElement('video');

              video.onloadedmetadata = () => {
                resolve(invokeCallback(video.videoWidth, video.videoHeight));
              };

              video.onerror = () => {
                resolve(invokeCallback());
              };

              video.src = result;
            });
          }

          return invokeCallback();
        },
        () => {
          navigation.pop();
        },
      );
    }, [allowedExtensions, navigation, onSelected, type, withObjectSize]);

    return (
      <TouchableNativeFeedback onPress={() => navigation.pop()}>
        <View
          css={`
            flex: 1;
          `}
        />
      </TouchableNativeFeedback>
    );
  },
  {
    animationEnabled: false,
    cardStyle: {
      backgroundColor: 'transparent',
    },
    headerShown: false,
    detachPreviousScreen: false,
  },
);

export default CameraScreenPage;
